import { useLayoutEffect, useState } from "react";

/** Available page height below the actual header/filters, inside the shell padding. */
export function useWorkspaceViewport<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null);

  useLayoutEffect(() => {
    if (!element) return;
    const page = element.closest<HTMLElement>(".workspace-page-content");
    if (!page) return;
    const viewport = window.visualViewport;
    const measure = () => {
      const padding = getComputedStyle(page);
      const top = element.getBoundingClientRect().top + window.scrollY;
      const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      const height =
        bottom -
        top -
        parseFloat(padding.paddingBottom) -
        (element === page ? parseFloat(padding.paddingTop) : 0);
      // A tall preceding form must not collapse an off-screen scroll region to zero.
      const value = height > 0 ? `${height}px` : "auto";
      if (element.style.getPropertyValue("--workspace-available-height") !== value) {
        element.style.setProperty("--workspace-available-height", value);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    for (let node: HTMLElement | null = element; node; node = node.parentElement) {
      observer.observe(node);
      for (
        let sibling = node.previousElementSibling;
        sibling;
        sibling = sibling.previousElementSibling
      ) {
        if (sibling instanceof HTMLElement) observer.observe(sibling);
      }
    }
    window.addEventListener("resize", measure);
    viewport?.addEventListener("resize", measure);
    viewport?.addEventListener("scroll", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      viewport?.removeEventListener("resize", measure);
      viewport?.removeEventListener("scroll", measure);
      element.style.removeProperty("--workspace-available-height");
    };
  }, [element]);

  return setElement;
}
